import { useRef, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { stashBlob } from '@/previs/audioBlobStore.ts';
import { getClipAbsoluteStart, resolveClipPlacement } from '@/store/projectDerived.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { AudioClipBar } from './AudioClipBar.tsx';
import { Playhead } from './Playhead.tsx';
import { LANE_COLORS, getRecordingMimeType } from '@/audio/audioConstants.ts';
import { capabilities } from '@/capabilities.ts';
import { sendToNative } from '@/bridge/embeddedBridge.ts';
import styles from './AudioLane.module.css';

const LANE_LABEL_KEYS = ['audioLaneLabelDialog', 'audioLaneLabelMusic', 'audioLaneLabelSfx', 'audioLaneLabelVoiceover'];

export function AudioLane() {
  const { t } = useTranslation('timeline');
  const isMobile = useMobileLayout();
  const LANE_HEIGHT = isMobile ? 24 : 28;
  const project = useProjectStore(s => s.project);
  const audioClips = useAudioStore(s => s.audioClips);
  const pixelsPerSecond = useUiStore(s => s.pixelsPerSecond);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const addAudioClip = useAudioStore(s => s.addAudioClip);
  const scrollLeft = useUiStore(s => s.scrollLeft);
  const setScrollLeft = useUiStore(s => s.setScrollLeft);
  const audioWorkspaceOpen = useUiStore(s => s.audioWorkspaceOpen);
  const targetLane = useUiStore(s => s.audioWorkspaceTargetLane);
  const setTargetLane = useUiStore(s => s.setAudioWorkspaceTargetLane);
  const selectedAudioClipId = useProjectStore(s => s.selectedAudioClipId);
  const selectAudioClip = useProjectStore(s => s.selectAudioClip);
  const play = usePlaybackStore(s => s.play);
  const pause = usePlaybackStore(s => s.pause);
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  const getViewDuration = useProjectStore(s => s.getViewDuration);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  const readOnly = useProjectStore(s => s.readOnly);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollSource = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Track container width for viewport windowing
  const containerWidthRef = useRef(1200);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    containerWidthRef.current = el.clientWidth;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) containerWidthRef.current = entry.contentRect.width;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const setIsRecording = useUiStore(s => s.setIsRecording);

  // Recording state
  const [countdown, setCountdown] = useState<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStartTimeRef = useRef(0);

  if (!project) return null;

  const viewOffset = getViewOffset();
  const viewDuration = getViewDuration();
  const totalWidth = viewDuration * pixelsPerSecond;
  const hasClips = audioClips.length > 0;
  // Expand to 4 channels when Audio Workspace is open (desktop only), single row on mobile
  const expanded = audioWorkspaceOpen && !isMobile;
  const mobileSingleLane = audioWorkspaceOpen && isMobile;
  const laneContentHeight = expanded ? LANE_HEIGHT * 4 : LANE_HEIGHT;
  const visibleHeight = laneContentHeight;

  // Always show all clips, filter by scene time range (+ lane filter on mobile)
  const viewEnd = viewOffset + viewDuration;
  const laneFiltered = mobileSingleLane ? audioClips.filter(c => c.lane === targetLane) : audioClips;
  const timeFiltered = laneFiltered.filter(c => {
    const absStart = getClipAbsoluteStart(c, project);
    const clipEnd = absStart + c.duration;
    return clipEnd > viewOffset && absStart < viewEnd;
  });

  // Viewport windowing: further filter to clips visible in the scroll viewport
  const BUFFER_PX = 300;
  const visibleClips = timeFiltered.length > 100
    ? timeFiltered.filter(c => {
        const absStart = getClipAbsoluteStart(c, project);
        const leftPx = (absStart - viewOffset) * pixelsPerSecond;
        const rightPx = (absStart + c.duration - viewOffset) * pixelsPerSecond;
        return rightPx >= scrollLeft - BUFFER_PX && leftPx <= scrollLeft + containerWidthRef.current + BUFFER_PX;
      })
    : timeFiltered;

  // Sync scroll from store (when ShotLane scrolls)
  useEffect(() => {
    if (isScrollSource.current) return;
    if (scrollRef.current && Math.abs(scrollRef.current.scrollLeft - scrollLeft) > 1) {
      scrollRef.current.scrollLeft = scrollLeft;
    }
  }, [scrollLeft]);

  // When audio lane is scrolled by user, push to store so ShotLane follows
  const onAudioScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const sl = scrollRef.current.scrollLeft;
    isScrollSource.current = true;
    setScrollLeft(sl);
    isScrollSource.current = false;
  }, [setScrollLeft]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    // Files only, like every other drop site: accepting internal/text drags armed the lane
    // highlight for drags it can never handle (onDrop filters to audio files anyway).
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => setIsDragging(false), []);

  const onDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('audio/') || f.name.match(/\.(wav|mp3|ogg|m4a|aac)$/i)
    );

    if (files.length === 0) return;

    const rect = scrollRef.current?.getBoundingClientRect();
    const sl = scrollRef.current?.scrollLeft ?? 0;
    const dropX = rect ? e.clientX - rect.left + sl : 0;
    const { getViewOffset: gvo } = useProjectStore.getState();
    const dropViewOffset = gvo();
    const dropAbsoluteTime = Math.max(dropViewOffset, dropX / pixelsPerSecond + dropViewOffset);

    const { audioWorkspaceOpen: wsOpen, audioWorkspaceTargetLane: wsLane } = useUiStore.getState();
    let dropLane = wsLane;
    if (wsOpen && rect) {
      const dropY = e.clientY - rect.top;
      dropLane = Math.min(3, Math.max(0, Math.floor(dropY / LANE_HEIGHT))) as 0 | 1 | 2 | 3;
    }
    const targetLane = dropLane;

    const proj = useProjectStore.getState().project;
    for (const file of files) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const rawData = new Uint8Array(arrayBuffer);

        const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');
        const audioBuffer = await decodeAudio(rawData);
        const peaks = generatePeaks(audioBuffer);

        const newId = useProjectStore.getState()._allocId();
        stashBlob(newId, rawData);

        const placement = proj ? resolveClipPlacement(dropAbsoluteTime, proj) : { shotId: 0, shotOffset: dropAbsoluteTime };
        addAudioClip({
          id: newId,
          label: file.name.replace(/\.[^.]+$/, ''),
          shotId: placement.shotId,
          shotOffset: placement.shotOffset,
          duration: audioBuffer.duration,
          volume: 0.75,
          bufferOffset: 0,
          audioBuffer,
          mimeType: file.type || 'audio/wav',
          lane: targetLane,
          isMuted: false,
          fadeInTime: 0,
          fadeOutTime: 0,
          color: LANE_COLORS[targetLane],
          peaks,
        });
      } catch (err) {
        console.warn(`Failed to import audio file "${file.name}":`, err);
      }
    }
  }, [pixelsPerSecond, addAudioClip]);

  // ── Recording ─────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      for (let i = 3; i >= 1; i--) {
        setCountdown(i);
        await new Promise(r => setTimeout(r, 800));
      }
      setCountdown(null);

      const recMime = getRecordingMimeType();
      const mediaRecorder = new MediaRecorder(stream, recMime ? { mimeType: recMime } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      recordChunksRef.current = [];
      recordStartTimeRef.current = currentTime;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        // Restore iOS audio session after mic recording
        if (capabilities.embeddedAudio) sendToNative('restoreAudioSession', {});

        const blob = new Blob(recordChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        const rawData = new Uint8Array(arrayBuffer);

        try {
          const { decodeAudio, generatePeaks, encodeAudioBufferToWav } = await import('@/audio/audioProcessor.ts');
          const audioBuffer = await decodeAudio(rawData);
          const peaks = generatePeaks(audioBuffer);
          // Convert to WAV — Unity/FMOD can't decode AAC (audio/mp4) reliably
          const wavData = encodeAudioBufferToWav(audioBuffer);

          const uiState = useUiStore.getState();
          const audioState = useAudioStore.getState();
          const recLane = uiState.recordingLane;
          const recordStart = recordStartTimeRef.current;
          const recordDuration = audioBuffer.duration;

          // Push a single undo snapshot for the entire operation
          useUndoStore.getState().pushSnapshot();

          const proj = useProjectStore.getState().project;

          // Check if playhead was inside an existing clip in this lane
          const hitClip = proj ? audioState.audioClips.find(c => {
            const absStart = getClipAbsoluteStart(c, proj);
            return c.lane === recLane &&
              recordStart > absStart &&
              recordStart < absStart + c.duration;
          }) : undefined;

          if (hitClip && proj) {
            // Split the existing clip at the record start point (convert to shot-relative offset)
            const hitClipShotStart = getClipAbsoluteStart(hitClip, proj) - hitClip.shotOffset;
            const splitOffset = recordStart - hitClipShotStart;
            audioState.splitAudioClip(hitClip.id, splitOffset, { pushUndo: false });
          }

          // Create the new recording clip
          const newId = useProjectStore.getState()._allocId();
          stashBlob(newId, wavData);

          const recPlacement = proj ? resolveClipPlacement(recordStart, proj) : { shotId: 0, shotOffset: recordStart };
          audioState.addAudioClip({
            id: newId,
            label: t('recordingLabel', { time: new Date().toLocaleTimeString() }),
            shotId: recPlacement.shotId,
            shotOffset: recPlacement.shotOffset,
            duration: recordDuration,
            volume: 0.75,
            bufferOffset: 0,
            audioBuffer,
            mimeType: 'audio/wav',
            lane: recLane,
            isMuted: false,
            fadeInTime: 0,
            fadeOutTime: 0,
            color: LANE_COLORS[Math.min(recLane, LANE_COLORS.length - 1)],
            peaks,
          }, { pushUndo: false });

          // Auto-comment on the shot at record start (reviewers only)
          if (!useProjectStore.getState().isCreator) {
            const shotAtRec = useProjectStore.getState().getShotAtTime(recordStart);
            if (shotAtRec) {
              const { reviewerName } = useCommentStore.getState();
              const who = reviewerName || 'Creator';
              useCommentStore.getState().addComment(shotAtRec.id, null, t('voiceOverCommentAuto', { who }));
            }
          }
        } catch (err) {
          console.error('Failed to process recording:', err);
          recordChunksRef.current = [];
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true, stopRecordingRef.current);
      play();
    } catch (err) {
      console.error('Microphone access denied:', err);
      setCountdown(null);
    }
  }, [currentTime, play, setIsRecording, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false, null);
    pause();
  }, [pause, setIsRecording]);

  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;

  const startRecordingRef = useRef(startRecording);
  startRecordingRef.current = startRecording;

  // Register startRecording in uiStore so other components can trigger it
  const setStartRecordingFn = useUiStore(s => s.setStartRecordingFn);
  useEffect(() => {
    setStartRecordingFn(() => startRecordingRef.current());
    return () => setStartRecordingFn(null);
  }, [setStartRecordingFn]);

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className={styles.audioLaneWrapper} style={{ height: visibleHeight }}>
      {/* Countdown overlay */}
      {countdown !== null && (
        <div className={styles.countdownOverlay}>{countdown}</div>
      )}

      {/* Sidebar — "AUDIO" label (collapsed), single lane label (mobile), or 4 lane labels (expanded) */}
      <div className={styles.sidebar}>
        {expanded ? (
          LANE_LABEL_KEYS.map((key, i) => {
            const label = t(key);
            return (
              <button
                key={key}
                className={`${styles.sidebarLabel} ${targetLane === i ? styles.sidebarLabelActive : ''}`}
                style={{ height: LANE_HEIGHT }}
                onClick={() => setTargetLane(i as 0 | 1 | 2 | 3)}
                title={t('selectLaneTitle', { lane: label })}
              >
                {label}
              </button>
            );
          })
        ) : mobileSingleLane ? (
          <div className={styles.sidebarStatic}>
            <span className={styles.channelName}>{t(LANE_LABEL_KEYS[targetLane])}</span>
          </div>
        ) : (
          /* Collapsed: blank spacer — the AUDIO gutter button directly above already
             labels and toggles this lane, so a second "AUDIO" word here is redundant. */
          <div className={styles.sidebarStatic} aria-hidden="true" />
        )}
      </div>

      {/* Scrollable audio area */}
      <div
        className={`${styles.scrollArea} ${isDragging ? styles.dropActive : ''}`}
        ref={scrollRef}
        onScroll={onAudioScroll}
        data-file-drop-target={readOnly ? undefined : ''}
        onDragOver={readOnly ? undefined : onDragOver}
        onDragLeave={readOnly ? undefined : onDragLeave}
        onDrop={readOnly ? undefined : onDrop}
      >
        <div
          className={styles.laneInner}
          style={{ width: totalWidth, height: laneContentHeight }}
          onClick={() => selectAudioClip(null)}
        >
          {/* Lane dividers (only when expanded via Audio Workspace) */}
          {expanded && [1, 2, 3].map(i => (
            <div
              key={`lane-div-${i}`}
              className={styles.laneDivider}
              style={{ top: i * LANE_HEIGHT }}
            />
          ))}

          {/* Audio clips */}
          {visibleClips.map(clip => (
            <AudioClipBar
              key={clip.id}
              clip={clip}
              pixelsPerSecond={pixelsPerSecond}
              laneHeight={LANE_HEIGHT}
              viewOffset={viewOffset}
              viewEnd={viewEnd}
              expanded={expanded}
              isSelected={clip.id === selectedAudioClipId}
              onSelect={selectAudioClip}
            />
          ))}

          {/* Playhead */}
          <Playhead height={laneContentHeight} />

          {/* Drop hint */}
          {!readOnly && !hasClips && !isDragging && (
            <div className={styles.hint}>{t('dropAudioFilesHere')}</div>
          )}
          {!readOnly && isDragging && (
            <div className={styles.dropOverlay}>{t('dropToImport')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
